/**
 * Import rules: the stored decisions a catalogue import runs on (plan §12.1 steps 2–3).
 * Everything that is specific to one business (which category means which role, how its
 * expiry-state SKUs are spelt, how recipe twins are named) is **configuration** here, never
 * code (D-007, D-026). Decisions made in the review queue are appended to `decisions`, so
 * the next run reproduces them.
 * @module
 */
import { z } from 'zod';
import { positiveQtyString } from '../quantity/index.js';
import { centsSchema } from '../money/index.js';
import { uomCode } from '../uom/index.js';
import { productRoleSchema, productRolesSchema } from '../catalogue/roles.js';
import {
  describeStateRuleIssue,
  stateKeySchema,
  validateStateRule,
} from '../catalogue/state-rules.js';

/** Format of {@link ImportRules}; bumped on a breaking change to the shape. */
export const IMPORT_RULES_FORMAT = 1;

const text = z.string().min(1);
const sourceRefSchema = z
  .string()
  .regex(/^(item|variant|category):.+$/, 'Expected item:, variant: or category:');

/** Maps category names to roles. Names compare trimmed and case-insensitively. */
export const categoryRoleMappingSchema = z.object({
  match: z.enum(['exact', 'prefix']),
  value: text,
  roles: productRolesSchema,
});
/** One category → roles mapping. */
export type CategoryRoleMapping = z.infer<typeof categoryRoleMappingSchema>;

/**
 * The mapping a category name falls under: the first match, trimmed and
 * case-insensitive (`exact` compares the whole name, `prefix` its start). -1 = none.
 */
export function matchCategoryMapping(
  mappings: readonly CategoryRoleMapping[],
  categoryName: string,
): number {
  const name = categoryName.trim().toLowerCase();
  return mappings.findIndex((m) => {
    const value = m.value.trim().toLowerCase();
    return m.match === 'exact' ? name === value : name.startsWith(value);
  });
}

/** `review` = the item cannot be classified until a person decides. */
const rolesOrReview = z.union([z.literal('review'), productRolesSchema]);

/** One state of the expiry-state SKU pattern, with the state rule it becomes. */
export const stateDefinitionSchema = z.object({
  key: stateKeySchema,
  /** Suffix of the state's SKU, compared in canonical SKU form (e.g. `-F`). */
  skuSuffix: text,
  /** Suffixes of the state's item name, case-insensitive (e.g. ` - Fresh`, ` (FRESH)`). */
  nameSuffixes: z.array(text).min(1),
  fromDaysBeforeBestBefore: z.int().min(-3650).max(3650).nullable(),
  sellable: z.boolean(),
  markdownBp: z.int().min(0).max(10_000),
  usableAsIngredient: z.boolean(),
});
/** One state definition. */
export type StateDefinition = z.infer<typeof stateDefinitionSchema>;

/** A barcode for a SKU from another channel's product export (plan §12: re-sourcing). */
export const barcodeSourceSchema = z.object({
  sku: text,
  gtin: text,
  /** Where it came from, e.g. `web shop export 2026-09-20`. */
  source: text,
});
/** One re-sourcing row. */
export type BarcodeSource = z.infer<typeof barcodeSourceSchema>;

/** A unit conversion to plan; `itemRef` limits it to that item's product(s). */
export const uomConversionRuleSchema = z.object({
  itemRef: sourceRefSchema.optional(),
  from: uomCode,
  to: uomCode,
  factor: positiveQtyString,
});
/** One conversion rule. */
export type UomConversionRule = z.infer<typeof uomConversionRuleSchema>;

/** A decision a person made in the review queue, stored so the next run reproduces it. */
export const decisionSchema = z.discriminatedUnion('kind', [
  /** Leave this item or variant out (it stays accounted for, with the reason). */
  z.object({ kind: z.literal('ignore'), reason: text }),
  /** Roles for an item, or for every item in a category. */
  z.object({ kind: z.literal('set_roles'), roles: productRolesSchema }),
  /** This twin (an item) holds the recipe of that item. */
  z.object({ kind: z.literal('pair_twin'), partnerItemRef: sourceRefSchema }),
  /** This item only looks like a recipe twin: import it as an ordinary item. */
  z.object({ kind: z.literal('not_twin') }),
  /** This item is the `state` member of expiry-state group `group` (any label). */
  z.object({ kind: z.literal('state_member'), group: text, state: stateKeySchema }),
  /** This item only looks like an expiry-state SKU: import it as an ordinary item. */
  z.object({ kind: z.literal('not_state_member') }),
  /** A variant's SKU replaced (`null` = import it without a SKU). */
  z.object({ kind: z.literal('set_sku'), sku: z.string().nullable() }),
  /** A variant's barcode replaced (`null` = import it without a GTIN). */
  z.object({ kind: z.literal('set_gtin'), gtin: z.string().nullable() }),
  /** An item's base unit. */
  z.object({ kind: z.literal('set_base_uom'), uom: uomCode }),
  /** The baker has seen the rounded quantities of this item's recipe. */
  z.object({ kind: z.literal('accept_quantities') }),
  /** Recipes that use this item use that item (or variant) instead. */
  z.object({
    kind: z.literal('resolve_component'),
    itemRef: sourceRefSchema,
    variantRef: sourceRefSchema.optional(),
  }),
  /** Recipes that use this item leave it out. */
  z.object({ kind: z.literal('drop_component') }),
  /** A standard cost per base unit, entered by a person. */
  z.object({ kind: z.literal('set_cost'), costCents: centsSchema }),
]);
/** A decision. */
export type Decision = z.infer<typeof decisionSchema>;
/** Kinds of {@link Decision}. */
export type DecisionKind = Decision['kind'];

/** A decision about one source record (`item:…`, `variant:…` or `category:…`). */
export const manualDecisionSchema = z.object({
  sourceRef: sourceRefSchema,
  decision: decisionSchema,
});
/** A stored decision. */
export type ManualDecision = z.infer<typeof manualDecisionSchema>;

/** Every {@link DecisionKind}, in schema order. */
export const DECISION_KINDS = [
  'ignore',
  'set_roles',
  'pair_twin',
  'not_twin',
  'state_member',
  'not_state_member',
  'set_sku',
  'set_gtin',
  'set_base_uom',
  'accept_quantities',
  'resolve_component',
  'drop_component',
  'set_cost',
] as const satisfies readonly DecisionKind[];
// Compile-time proof that the list names every kind.
const _allKinds: DecisionKind extends (typeof DECISION_KINDS)[number] ? true : false = true;
void _allKinds;

/**
 * Decisions about the same record in the same slot replace each other (a later
 * `not_twin` replaces an earlier `pair_twin`).
 */
export const DECISION_SLOTS: Readonly<Record<DecisionKind, string>> = {
  ignore: 'ignore',
  set_roles: 'roles',
  pair_twin: 'twin',
  not_twin: 'twin',
  state_member: 'state',
  not_state_member: 'state',
  set_sku: 'sku',
  set_gtin: 'gtin',
  set_base_uom: 'uom',
  accept_quantities: 'quantities',
  resolve_component: 'component',
  drop_component: 'component',
  set_cost: 'cost',
};

/** The stored rules of a catalogue import. */
export const importRulesSchema = z.object({
  format: z.literal(IMPORT_RULES_FORMAT),
  /** Increases with every change (decisions included); the plan records it. */
  revision: z.int().min(0),
  categoryRoles: z.object({
    /** First match wins. */
    mappings: z.array(categoryRoleMappingSchema),
    /** Items in a category no mapping matches. */
    unmapped: rolesOrReview,
    /** Items with no category. */
    uncategorised: rolesOrReview,
    /**
     * An item other recipes use gets `intermediate` (if it has a recipe) or `ingredient`
     * unless it already has a role that can be consumed.
     */
    addComponentRoles: z.boolean(),
  }),
  stateGroups: z
    .object({
      /** Name of the state rule the groups share. */
      ruleName: text,
      /** In order, first = fresh. Empty = detection off. */
      states: z.array(stateDefinitionSchema),
      /** The state whose barcode is the product's GTIN; `null` = none. */
      gtinState: stateKeySchema.nullable(),
      /** All members of a group must share a category. */
      requireSameCategory: z.boolean(),
    })
    .superRefine((g, ctx) => {
      const rule = g.states.map((s) => ({ ...s, channels: [] }));
      if (rule.length > 0) {
        for (const issue of validateStateRule(rule)) {
          ctx.addIssue({
            code: 'custom',
            message: describeStateRuleIssue(issue),
            path: ['states'],
          });
        }
      }
      const suffixes = g.states.map((s) => s.skuSuffix.toUpperCase());
      if (new Set(suffixes).size !== suffixes.length) {
        ctx.addIssue({ code: 'custom', message: 'SKU suffixes must differ', path: ['states'] });
      }
      if (g.gtinState !== null && !g.states.some((s) => s.key === g.gtinState)) {
        ctx.addIssue({
          code: 'custom',
          message: 'gtinState must be one of the states',
          path: ['gtinState'],
        });
      }
    }),
  twins: z.object({
    /** SKU suffixes of a recipe twin, compared in canonical SKU form (e.g. `-REC`). */
    skuSuffixes: z.array(text),
    /** Name suffixes, case-insensitive; spaces and hyphens left before them are trimmed. */
    nameSuffixes: z.array(text),
  }),
  /** Barcodes from other channels, used only to re-source a damaged barcode by SKU. */
  barcodeSources: z.array(barcodeSourceSchema),
  ignore: z.object({
    /** Items the source has deleted are ignored (reason stored). */
    deletedItems: z.boolean(),
  }),
  uom: z.object({
    /** Base unit of an item sold by weight. */
    weighedBaseUom: uomCode,
    /** Base unit of everything else. */
    countBaseUom: uomCode,
    conversions: z.array(uomConversionRuleSchema),
  }),
  recipes: z.object({
    /**
     * A recipe whose measured quantities (kg, l) are **all** multiples of this looks
     * rounded and goes to the baker; `null` = off.
     */
    roundedQuantityStep: positiveQtyString.nullable(),
  }),
  expiry: z.object({
    /** Products made from expiry-state groups track expiry. */
    stateGroups: z.boolean(),
    /** Made-ahead products with a recipe track expiry. */
    madeAheadRecipes: z.boolean(),
    /** Products with any of these roles track expiry. */
    roles: z.array(productRoleSchema),
  }),
  decisions: z.array(manualDecisionSchema),
});
/** The stored rules of a catalogue import. */
export type ImportRules = z.infer<typeof importRulesSchema>;

/** Neutral defaults: nothing business-specific is assumed; unknown categories go to review. */
export function defaultImportRules(): ImportRules {
  return {
    format: IMPORT_RULES_FORMAT,
    revision: 0,
    categoryRoles: {
      mappings: [],
      unmapped: 'review',
      uncategorised: 'review',
      addComponentRoles: true,
    },
    stateGroups: {
      ruleName: 'Expiry states',
      states: [],
      gtinState: null,
      requireSameCategory: true,
    },
    twins: { skuSuffixes: [], nameSuffixes: [] },
    barcodeSources: [],
    ignore: { deletedItems: true },
    uom: { weighedBaseUom: 'kg', countBaseUom: 'ea', conversions: [] },
    recipes: { roundedQuantityStep: null },
    expiry: { stateGroups: true, madeAheadRecipes: true, roles: [] },
    decisions: [],
  };
}

/**
 * Stores decisions: each replaces an earlier decision about the same record in the same
 * slot (see {@link DECISION_SLOTS}), otherwise it is appended. Bumps `revision` once.
 * Returns new rules; the input is not changed.
 */
export function applyDecisions(
  rules: ImportRules,
  decisions: readonly ManualDecision[],
): ImportRules {
  return applyDecisionChanges(rules, decisions).rules;
}

/** Takes back the stored decision in the slot of `kind` about `sourceRef`. */
export interface DecisionRevocation {
  sourceRef: string;
  revoke: DecisionKind;
}

/** A change to the stored decisions: a new decision, or a revocation. */
export type DecisionChange = ManualDecision | DecisionRevocation;

/**
 * Applies decisions and revocations in order (see {@link applyDecisions}); a revocation
 * removes the decision in its slot. Bumps `revision` once. `notFound` lists revocations
 * whose slot held no decision (they change nothing). The input is not changed.
 */
export function applyDecisionChanges(
  rules: ImportRules,
  changes: readonly DecisionChange[],
): { rules: ImportRules; notFound: DecisionRevocation[] } {
  const slot = (sourceRef: string, kind: DecisionKind) => `${sourceRef}|${DECISION_SLOTS[kind]}`;
  let next = [...rules.decisions];
  const notFound: DecisionRevocation[] = [];
  for (const change of changes) {
    if ('revoke' in change) {
      const target = slot(change.sourceRef, change.revoke);
      const kept = next.filter((e) => slot(e.sourceRef, e.decision.kind) !== target);
      if (kept.length === next.length) notFound.push(change);
      next = kept;
      continue;
    }
    const d = manualDecisionSchema.parse(change);
    const at = next.findIndex(
      (e) => slot(e.sourceRef, e.decision.kind) === slot(d.sourceRef, d.decision.kind),
    );
    if (at >= 0) next[at] = d;
    else next.push(d);
  }
  return { rules: { ...rules, revision: rules.revision + 1, decisions: next }, notFound };
}
